import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import DialogView from '../../../components/dialog-view/DialogView';
import removeQueryParams from '../../../utils/routing/remove-query-params';

import TaskDetailsViewContent, { InvoiceError } from './TaskDetailsViewContent';
import {
  CALENDAR_ROUTE,
  EDIT_TASK_ROUTE,
  TASKS_DETAILS_DIALOG_ROUTE,
  TASKS_ROUTE,
  WORKPLAN_ROUTE
} from '../../../utils/constants';
import NavigationHeader from './navigation-header/NavigationHeader';

import { isDevice } from '../../../utils/device-handling/deviceDetectionUtils';
import useWorkTaskLock from '../../../hooks/useWorkTaskLock';
import { QueryParams } from '../../../models/QueryParams';
import useQuery from '../../../hooks/useQuery';
import useChecklistLock from '../../../hooks/useChecklistLock';
import { useNavigate } from '../../../hooks/useNavigate';
import { useDispatch, useSelector } from 'react-redux';
import { selectShowOrderGoods, setShowOrderGoods } from '../../../stateManagement/reducers/orderGoodsReducer';
import OrderGoodsView from '../../goods-view/order-goods-dialog/OrderGoodsView';
import { BaseWorkTaskTypeEnum, LocationDTO, WorkInvoiceDTO } from '../../../api/api';
import SubWorkTaskService from '../../../services/SubWorkTaskService';
import TaskService from '../../../services/TaskService';
import NotificationService from '../../../services/NotificationService';
import { WorkTaskDTOExtended } from '../../../models/TaskType';
import { log } from '../../../utils/logging/log';
import MeterTaskDetailsViewContent from './meter-task-details-view/MeterTaskDetailsViewContent';
import { workInvoiceResponseDTOPopulated } from './work-invoice-step/WorkInvoiceHelper';
import GoodsService from '../../../services/GoodsService';
import TimeRegistrationService from '../../../services/TimeRegistrationService';

const TaskDetailsView = () => {
  const [toolsOpen, setToolsOpen] = useState(false);
  const showOrderGoods = useSelector(selectShowOrderGoods);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { id, type: detailType } = useQuery<QueryParams>();
  const [defaultActivePanelId, setDefaultActivePanelId] = useState('1');
  const [taskLocation, setTaskLocation] = useState<LocationDTO>();
  const [isLoading, setIsLoading] = useState(false);
  const [workInvoiceId, setWorkInvoiceId] = useState<number>();
  const [workInvoice, setWorkInvoice] = useState<WorkInvoiceDTO>();
  const [showWorkInvoiceError, setShowWorkInvoiceError] = useState<InvoiceError>(InvoiceError.NONE);

  const [task, setTask] = useState<WorkTaskDTOExtended>();
  const { addWorkTaskLock, releaseWorkTaskLock } = useWorkTaskLock();

  const { releaseChecklistLock } = useChecklistLock();

  const fullscreenTask = isDevice();
  const prevLocation = location?.state?.prevLocation?.pathname ?? undefined;
  const header = prevLocation?.includes(EDIT_TASK_ROUTE)
    ? 'Opret opgave'
    : prevLocation?.includes(WORKPLAN_ROUTE)
    ? 'Opgaver'
    : prevLocation?.includes(CALENDAR_ROUTE)
    ? 'Kalender'
    : prevLocation?.includes(TASKS_ROUTE)
    ? 'Opgaveliste'
    : '';

  const lockTask = useCallback(
    (worktaskId: number) => {
      addWorkTaskLock(worktaskId);
    },
    [addWorkTaskLock]
  );

  const fetchTask = useCallback(async () => {
    if (!id || !detailType) return;
    setIsLoading(true);
    releaseWorkTaskLock(parseInt(id));
    releaseChecklistLock();
    try {
      let task: WorkTaskDTOExtended = {};
      if (detailType === BaseWorkTaskTypeEnum.SubWorkTask) {
        const subTask = await SubWorkTaskService.getSubTask(parseInt(id));
        if (subTask.mainTask) {
          task = subTask.mainTask;
          // Map subTask props as mainTask props
          task.subTaskId = subTask.workTaskId;
          task.mainTaskAssignedToName = task.assignedToName;
          task.mainTaskStatus = task.status;
          task.createdByName = subTask.createdByName;
          task.createdByEmail = subTask.createdByEmail;
          task.assignedToName = subTask.assignedToName;
          task.assignedToEmail = subTask.assignedToEmail;
          task.taskType = { ...subTask.taskType, externalResources: subTask.mainTask.taskType?.externalResources };
          task.status = subTask.status;
          task.subWorkTasks = [];
        }
      } else {
        task = await TaskService.getTask(parseInt(id));
      }
      task.taskLocation && setTaskLocation(task.taskLocation);
      setTask(task);
    } catch (error) {
      NotificationService.error('Arbejdskortet kunne ikke hentes', 5000);
      log(error);
    } finally {
      lockTask(parseInt(id));
      setIsLoading(false);
    }
    // checklistReadOnly & releaseChecklistLock should not trigger re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, detailType, releaseWorkTaskLock, lockTask]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  const handleCloseDialog = useCallback(() => {
    releaseWorkTaskLock(parseInt(id ?? ''));
    releaseChecklistLock();

    if (prevLocation?.includes(EDIT_TASK_ROUTE)) {
      navigate(-1);
    } else {
      navigate(removeQueryParams(['id', 'type'], TASKS_DETAILS_DIALOG_ROUTE), {
        state: { prevLocation: location }
      });
    }
  }, [id, location, navigate, prevLocation, releaseChecklistLock, releaseWorkTaskLock]);

  const handleCloseTask = useCallback(() => {
    releaseWorkTaskLock(parseInt(id ?? ''));
    releaseChecklistLock();
    navigate(-1);
  }, [id, navigate, releaseChecklistLock, releaseWorkTaskLock]);

  const closeOrderGoodsView = () => {
    setDefaultActivePanelId('5');
    dispatch(setShowOrderGoods(false));
  };

  const fetchWorkInvoiceNotification = useCallback(
    async (workInvoiceDTO?: WorkInvoiceDTO) => {
      if (workInvoiceDTO) setWorkInvoice(workInvoiceDTO);
      const _workInvoice = workInvoiceDTO ?? workInvoice;

      if (!id || !_workInvoice) return;

      if (!workInvoiceResponseDTOPopulated(_workInvoice)) {
        setShowWorkInvoiceError(InvoiceError.ALERT);
        return;
      }
      if (_workInvoice?.projectCategory === undefined || _workInvoice?.projectCategory === null) {
        setShowWorkInvoiceError(InvoiceError.NONE);
        return;
      }

      if (!_workInvoice?.projectId) {
        setShowWorkInvoiceError(InvoiceError.NOTIFICATION);
        return;
      }

      const goodsToMove = await GoodsService.checkIfWorkTaskHasConsumedItems(parseInt(id)).catch((err) => log(err));
      if (goodsToMove) {
        setShowWorkInvoiceError(InvoiceError.NOTIFICATION);
        return;
      }

      const timeToMove = await TimeRegistrationService.checkIfWorkTaskHasTimeRegistrations(parseInt(id)).catch((err) =>
        log(err)
      );
      if (timeToMove) {
        setShowWorkInvoiceError(InvoiceError.NOTIFICATION);
        return;
      }

      setShowWorkInvoiceError(InvoiceError.NONE);
    },
    [workInvoice, id]
  );

  if (showOrderGoods)
    return (
      <OrderGoodsView
        taskLocation={taskLocation}
        onClose={closeOrderGoodsView}
        orderGoodsToTask
        returnToTaskDetails={closeOrderGoodsView}
        taskTypeId={task?.taskType?.id}
      />
    );

  return (
    <>
      {fullscreenTask ? (
        <>
          <NavigationHeader onClick={handleCloseTask} title={header} />
          <StyledContent onClick={() => toolsOpen && setToolsOpen(false)}>
            {!task?.taskType?.isMeterTaskType && !task?.isMeterTask ? (
              <TaskDetailsViewContent
                hideCloseButton
                handleCloseTask={handleCloseTask}
                defaultActivePanelId={defaultActivePanelId}
                fetchTask={fetchTask}
                toolsOpen={toolsOpen}
                setToolsOpen={(open) => setToolsOpen(open)}
                setTask={(task) => setTask(task)}
                task={task}
                setWorkInvoice={setWorkInvoice}
                setWorkInvoiceId={setWorkInvoiceId}
                showWorkInvoiceError={showWorkInvoiceError}
                workInvoice={workInvoice}
                workInvoiceId={workInvoiceId}
                fetchWorkInvoiceNotification={fetchWorkInvoiceNotification}
              />
            ) : (
              <MeterTaskDetailsViewContent
                handleCloseTask={handleCloseTask}
                defaultActivePanelId={defaultActivePanelId}
                toolsOpen={toolsOpen}
                setToolsOpen={(open) => setToolsOpen(open)}
                fetchTask={fetchTask}
                setTask={(task) => setTask(task)}
                task={task}
                setWorkInvoice={setWorkInvoice}
                setWorkInvoiceId={setWorkInvoiceId}
                showWorkInvoiceError={showWorkInvoiceError}
                workInvoice={workInvoice}
                workInvoiceId={workInvoiceId}
                fetchWorkInvoiceNotification={fetchWorkInvoiceNotification}
                fetchTaskCallback={fetchTask}
              />
            )}
          </StyledContent>
        </>
      ) : (
        <DialogView handleClose={handleCloseDialog} onClick={() => toolsOpen && setToolsOpen(false)}>
          <StyledDialogContent>
            {!task?.taskType?.isMeterTaskType && !task?.isMeterTask ? (
              <TaskDetailsViewContent
                toolsOpen={toolsOpen}
                setToolsOpen={(open) => setToolsOpen(open)}
                handleCloseTask={handleCloseDialog}
                defaultActivePanelId={defaultActivePanelId}
                setTask={(task) => setTask(task)}
                fetchTask={fetchTask}
                task={task}
                setWorkInvoice={setWorkInvoice}
                setWorkInvoiceId={setWorkInvoiceId}
                showWorkInvoiceError={showWorkInvoiceError}
                workInvoice={workInvoice}
                workInvoiceId={workInvoiceId}
                fetchWorkInvoiceNotification={fetchWorkInvoiceNotification}
              />
            ) : (
              <MeterTaskDetailsViewContent
                handleCloseTask={handleCloseTask}
                defaultActivePanelId={defaultActivePanelId}
                toolsOpen={toolsOpen}
                setToolsOpen={(open) => setToolsOpen(open)}
                fetchTask={fetchTask}
                setTask={(task) => setTask(task)}
                task={task}
                setWorkInvoice={setWorkInvoice}
                setWorkInvoiceId={setWorkInvoiceId}
                showWorkInvoiceError={showWorkInvoiceError}
                workInvoice={workInvoice}
                workInvoiceId={workInvoiceId}
                fetchWorkInvoiceNotification={fetchWorkInvoiceNotification}
                fetchTaskCallback={fetchTask}
              />
            )}
          </StyledDialogContent>
        </DialogView>
      )}
    </>
  );
};

const StyledDialogContent = styled.div`
  padding: 0;
  height: 90vh;
  width: 85vw;
  max-width: 1400px;
  overflow: hidden;

  @media (max-width: 1200px) {
    width: 95vw;
  }
`;
const StyledContent = styled.div`
  height: 100%;
  width: 100vw;

  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: start;
  overflow: hidden;
`;

export default TaskDetailsView;
